"use client";

import { useId } from "react";
import { useNewsletterForm } from "@/app/ui/hooks/useNewsletterForm";

interface NewsletterSignupProps {
  variant?: "inline" | "card";
  className?: string;
}

export default function NewsletterSignup({ variant = "card", className = "" }: NewsletterSignupProps) {
  const { email, setEmail, status, message, handleSubmit } = useNewsletterForm();
  const inputId = useId();

  if (variant === "inline") {
    return (
      <div className={`${className}`}>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <label htmlFor={inputId} className="sr-only">Email address</label>
            <input
              id={inputId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              maxLength={100}
              aria-label="Email address for newsletter signup"
              disabled={status === "loading" || status === "success"}
              className="w-full px-4 py-2.5 rounded-lg bg-(--bg-card) border border-(--border-light) text-(--text-primary) placeholder:text-(--text-muted) focus:outline-none focus:ring-2 focus:ring-(--brand) disabled:opacity-60"
            />
          </div>
          <button
            type="submit"
            disabled={status === "loading" || status === "success"}
            className="px-6 py-2.5 rounded-lg bg-(--gold) text-(--text-on-gold) font-['Font-Bold'] hover:bg-(--brand) hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed!" : "Subscribe"}
          </button>
        </form>
        {status === "error" && (
          <p className="mt-2 text-sm text-(--text-error)">{message}</p>
        )}
        {status === "success" && (
          <p className="mt-2 text-sm text-(--gold-accent)">{message}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`theme-card-alt rounded-xl p-6 shadow-sm ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-(--brand)/20 flex items-center justify-center">
          <svg className="w-5 h-5 text-(--brand)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <div>
          <h3 className="font-['Font-Bold'] text-lg text-(--text-headline)">Stay in the Loop</h3>
          <p className="text-sm text-(--text-muted)">Get updates on our launch and latest news</p>
        </div>
      </div>
      
      {status === "success" ? (
        <div className="text-center py-4">
          <svg className="w-12 h-12 mx-auto text-(--gold) mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-(--text-secondary)">{message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <label htmlFor={inputId} className="sr-only">Email address</label>
            <input
              id={inputId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              maxLength={100}
              aria-label="Email address for newsletter signup"
              disabled={status === "loading"}
              className="w-full px-4 py-3 rounded-lg bg-(--bg-secondary) border border-(--border-light) text-(--text-primary) placeholder:text-(--text-muted) focus:outline-none focus:ring-2 focus:ring-(--brand) disabled:opacity-60"
            />
          </div>
          {status === "error" && (
            <p className="text-sm text-(--text-error)">{message}</p>
          )}
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full px-6 py-3 rounded-lg bg-(--gold) text-(--text-on-gold) font-['Font-Bold'] hover:bg-(--brand) hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === "loading" ? "Subscribing..." : "Subscribe for Updates"}
          </button>
        </form>
      )}
    </div>
  );
}
